import { InformationCard, type EntityValue, Button } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  useHasPolicyProductConfig,
  usePolicyProductConfig,
} from '@folio/utils/usePolicyProductConfig';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import {
  customerControllerGetCustomerQueryOptions,
  customerControllerGetCustomerQueryKey,
  useBranchControllerForkBranch,
  useCustomerControllerEditCustomer,
  toKeyValueFields,
  keyValueFieldsToObject,
} from 'korint-sdk';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAccessibleCustomerIds } from '@folio/hooks/useAccessibleCustomerIds';

type CustomerInformationCardProps = {
  allowEdition?: boolean;
  onDeleteCustomer?: (customerId: string) => void;
};

export function CustomerInformationCardModule({
  allowEdition = true,
  onDeleteCustomer,
}: CustomerInformationCardProps) {
  const { policy } = usePolicyOnDashboard();

  const accessibleCustomerIds = useAccessibleCustomerIds(policy.customers);

  const hasInformationCardConfig = useHasPolicyProductConfig('customerInformations');
  if (!hasInformationCardConfig) {
    return null;
  }

  return (
    <>
      {accessibleCustomerIds.map(customerId => (
        <SingleCustomerCard
          key={customerId}
          customerId={customerId}
          allowEdition={allowEdition}
          onDelete={onDeleteCustomer}
          showRoleLabel={accessibleCustomerIds.length > 1}
        />
      ))}
    </>
  );
}

type SingleCustomerCardProps = {
  customerId: string;
  allowEdition: boolean;
  onDelete?: (customerId: string) => void;
  showRoleLabel: boolean;
};

function SingleCustomerCard({
  customerId,
  allowEdition,
  onDelete,
  showRoleLabel,
}: SingleCustomerCardProps) {
  const { branchId } = useAssertedParams();
  const { policy } = usePolicyOnDashboard();
  const customerInformationConfig = usePolicyProductConfig('customerInformations');
  const productCustomPath = usePolicyProductConfig('productCustomPath');
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const { data: customer } = useSuspenseQuery({
    ...customerControllerGetCustomerQueryOptions(customerId, { branchId }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { mutateAsync: editCustomer } = useCustomerControllerEditCustomer();

  async function updateEntity(key: string, value: EntityValue) {
    if (!customer) {
      console.warn('Tried to edit customer information card but customer is not defined');
      return;
    }

    await editCustomer({
      customerId: customer.id,
      data: {
        customFields: toKeyValueFields({ [key]: value }),
      },
      params: { branchId },
    });

    queryClient.invalidateQueries({
      queryKey: customerControllerGetCustomerQueryKey(customerId, { branchId }),
    });
  }

  const { mutateAsync: forkBranch } = useBranchControllerForkBranch();
  const navigate = useNavigate();

  async function redirectToForm() {
    if (!policy.id || !customer) {
      console.warn(
        'Tried to edit customer information card but policy.id or customer is not defined',
      );
      return;
    }
    if (!branchId) {
      navigate(`/mta${productCustomPath}/${customer.id}/${policy.id}/new`);
      return;
    }

    const branch = await forkBranch({
      policyId: policy.id,
      branchId,
      data: {
        branchType: 'NEW_BUSINESS',
      },
    });
    navigate(`/discovery${productCustomPath}/${customer.id}/${policy.id}/${branch.id}`);
  }

  const baseTitle = t(`${policy.productId}.customerInformations.title`);

  return (
    <InformationCard
      {...customerInformationConfig}
      title={`${baseTitle}${showRoleLabel && customer.role ? ` : ${t(`relationTypes.${customer.role}.label`)}` : ''}`}
      productId={policy.productId}
      entityType="customer"
      entity={customer}
      updateEntity={updateEntity}
      redirectToForm={redirectToForm}
      allowEdition={allowEdition}
      headerActions={
        onDelete ? (
          <Button
            variant="destructive-ghost"
            size="icon"
            onClick={() => onDelete(customerId)}
            title={t('customerInformationCard.deleteCustomerTooltip')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : undefined
      }
    />
  );
}
